import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiMail, FiCalendar, FiCheck, FiStar, FiFileText } from 'react-icons/fi';
import SafeIcon from './SafeIcon';

const CandidateModal = ({ candidate, onClose }) => {
  if (!candidate) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-slate-900 border border-slate-700 w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-800 flex justify-between items-start bg-slate-800/20">
            <div className="flex gap-4 items-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-indigo-900 flex items-center justify-center border-2 border-blue-400/30 text-2xl font-bold text-white shadow-[0_0_15px_rgba(59,130,246,0.3)]">
                {candidate.name.charAt(0)}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-100">{candidate.name}</h2>
                <p className="text-blue-400 text-sm font-medium mt-1">{candidate.email}</p>
                <div className="flex gap-2 mt-2">
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-slate-800 text-slate-300 border border-slate-700">
                    {candidate.stage}
                  </span>
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-green-500/10 text-green-400 border border-green-500/20">
                    Fit Score: {candidate.score}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-1 grid grid-cols-3 gap-6">
            <div className="col-span-2 space-y-6">
              <section>
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiStar} /> Skills Radar
                </h3>
                <div className="flex flex-wrap gap-2">
                  {candidate.tech.map(tech => (
                    <span key={tech} className="px-3 py-1 bg-slate-800/50 border border-slate-700 rounded-full text-sm text-slate-200">
                      {tech}
                    </span>
                  ))}
                  <span className="px-3 py-1 border border-dashed border-slate-700 rounded-full text-sm text-slate-500 hover:text-slate-300 cursor-pointer">
                    + Add Skill
                  </span>
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiFileText} /> AI Screening Notes
                </h3>
                <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-sm text-slate-300 leading-relaxed">
                  <p className="mb-2"><strong className="text-slate-200">Strengths:</strong> Strong background in required tech stack. Articulate communication style. Demonstrated leadership in previous role.</p>
                  <p><strong className="text-slate-200">Areas to Probe:</strong> Look into exact experience with large-scale data migrations. Ask for specific examples of conflict resolution.</p>
                </div>
              </section>
            </div>

            <div className="col-span-1 space-y-4">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Quick Actions</h3>
              <button className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white p-2.5 rounded-lg text-sm font-medium transition-colors">
                <SafeIcon icon={FiMail} /> Email Snippet
              </button>
              <button className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-200 p-2.5 rounded-lg text-sm font-medium transition-colors border border-slate-700">
                <SafeIcon icon={FiCalendar} /> Schedule Next
              </button>
              <button className="w-full flex items-center justify-center gap-2 bg-green-600/20 hover:bg-green-600/30 text-green-400 p-2.5 rounded-lg text-sm font-medium transition-colors border border-green-500/30 mt-4">
                <SafeIcon icon={FiCheck} /> Advance Stage
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CandidateModal;